// src/context/AuthContext.jsx
import React, { createContext, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import api from '../services/api';
import { ensureCategoryConstants } from '../config/categoryConstants'; // 454 (T-3): hydrate the DB-fed category maps after auth
// GES-252 P4b — role page visibility. /api/me already returns visible_pages (P2); until now
// the FE read only is_admin off it and threw the rest away. It is state from here on, and
// pageAccess owns the single decision function every screen asks.
import { pageVisible, tabVisible, firstVisiblePage } from '../config/pageAccess';
// GES-253 — per-user language. /api/me carries `language` (db/515: user_profiles.language); this
// context is where it becomes the running i18n language, because this is the only place that
// already knows WHO is signed in and when their profile arrived.
import i18n, { SUPPORTED_LANGUAGES, BASE_LANGUAGE, rememberLanguageHint } from '../i18n';
import { startLiveOverrides } from '../i18n/liveOverrides';

export const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser]       = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  // GES-252 P4b. `visiblePages: null` is NOT "empty" — it is "we do not know yet / we could
  // not find out", and it fails closed exactly like {} does. accessError distinguishes the
  // two for the user, because "your admin gave you no pages" and "we could not reach the
  // server" deserve different sentences. There is no fallback map anywhere in this file.
  const [visiblePages, setVisiblePages] = useState(null);
  const [roleName, setRoleName]         = useState(null);
  const [accessError, setAccessError]   = useState(null);
  const [accessReady, setAccessReady]   = useState(false);
  // GES-253. `language: null` is NOT 'en' — it is "this account has never chosen one" (no
  // user_profiles row, or db/515 not applied yet). The app then renders the BASE catalog, which is
  // English, and writes nothing back. Storing the null rather than collapsing it to 'en' is what
  // lets the switcher show the true state and the admin screen say "not set" instead of "English".
  const [language, setLanguageState] = useState(null);
  // Set when a language change could not be PERSISTED. The change still applies to this session —
  // refusing to switch would be worse — but the user is told it will not survive a reload.
  const [languageError, setLanguageError] = useState(null);

  // Round 4d (Codex S2 of 4c): boot generation. checkAuth/signin/signout each start a new generation; an answer that arrives for an
  // older one (StrictMode's first effect run, a sign-out while the boot reads are in flight) is dropped instead of writing a stale
  // user / role / pages / language over the current state.
  const genRef = useRef(0);

  useEffect(() => {
    checkAuth();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- boot check runs once; checkAuth is a per-render function
  }, []);

  async function loadProfile(gen = genRef.current) {
    const stale = () => gen !== genRef.current;
    try {
      const me = await api.getMe();
      if (stale()) return;
      // v0.43 — the SERVER shell preference is authoritative. Sync it into the localStorage flag the
      // App.jsx shell gate reads, and reload ONCE if that flips the current shell, so an admin can pin
      // a user to v1/v2. `shell_pref` null → leave the local default/opt-out alone. Steady state: no
      // reload (the flag already matches). Runs before the other sets so a mismatch never renders the
      // wrong shell for more than the initial frame.
      try {
        const pref = me.shell_pref;
        const cur = window.localStorage.getItem('shell_v2');   // 'off' = classic pinned; else default (v2)
        if (pref === 'v1' && cur !== 'off') { window.localStorage.setItem('shell_v2', 'off'); window.location.reload(); return; }
        if (pref === 'v2' && cur === 'off') { window.localStorage.removeItem('shell_v2'); window.location.reload(); return; }
      } catch (_) { /* localStorage unavailable → the gate keeps its own default */ }
      setIsAdmin(me.is_admin === true);
      // The server is the only source. A response without the field is a server we do not
      // understand, so it is treated as "no pages", not as "all pages".
      setVisiblePages(me.visible_pages && typeof me.visible_pages === 'object' ? me.visible_pages : {});
      setRoleName(me.role_name ?? null);
      // GES-253. The server is the only source here too. An unrecognised value is treated as "not
      // chosen" rather than passed to i18next, so a future third language in the database cannot
      // put a bundle-less locale on screen.
      const lang = SUPPORTED_LANGUAGES.includes(me.language) ? me.language : null;
      setLanguageState(lang);
      setLanguageError(null);
      // Not chosen -> the base catalog. That is the fallback language, not an assumed preference.
      // 47110 A7i: awaited, so the shell renders only once this language's catalog is in (no flash of another language); the
      // per-device hint then remembers it for the next boot's prefetch (it never decides what renders).
      await i18n.changeLanguage(lang || BASE_LANGUAGE);
      if (stale()) return;
      rememberLanguageHint(lang || BASE_LANGUAGE);
      startLiveOverrides(api, i18n);   // Language page: live text overrides (fail-safe, never blocks)
      setAccessError(null);
    } catch (err) {
      if (stale()) return;
      setIsAdmin(false);
      setVisiblePages(null);
      setRoleName(null);
      setLanguageState(null);
      setAccessError(err?.message || 'Could not load your access.');
      await i18n.changeLanguage(BASE_LANGUAGE);
    } finally {
      if (!stale()) setAccessReady(true);
    }
  }

  async function checkAuth() {
    const gen = ++genRef.current;
    const token = localStorage.getItem('accessToken');
    if (token) {
      api.setToken(token);
      try {
        // Round 4c: the three boot reads are independent (each needs only the token), so they go out together instead of
        // auth/me -> me -> category-constants in series (3 round trips -> 1). loadProfile still owns role/pages/language (the server
        // stays the authority), and the shell still renders only after all three settle. auth/me is the gate: if it fails the
        // profile just loaded is dropped with the token (signout() resets every access field). Unchanged from before: a failed /me or
        // category-constants read alone does not sign out (loadProfile records accessError, which fails closed; the constants keep their fallbacks).
        const [userData] = await Promise.all([
          api.getCurrentUser(),
          loadProfile(gen),
          ensureCategoryConstants(),   // 454: load the DB-fed category maps before the app renders forms
        ]);
        if (gen !== genRef.current) return;   // superseded (StrictMode re-run or sign-out): the newer generation owns the state
        setUser(userData.user);
      } catch (err) {
        if (gen !== genRef.current) return;
        signout();
        localStorage.removeItem('accessToken');
        api.clearToken();
      }
    }
    setLoading(false);
  }

  async function signin(email, password) {
    try {
      setError(null);
      const gen = ++genRef.current;
      const response = await api.signin(email, password);
      api.setToken(response.accessToken, response.refreshToken || response.session?.refresh_token);
      await loadProfile(gen);   // 47110: before setUser, so the shell first renders in the user's language
      setUser(response.user);
      await ensureCategoryConstants();   // 454: load the DB-fed category maps right after login
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }

  // 41039 — signup() REMOVED: no public self-service registration. Users are created by an admin in
  // the Supabase dashboard and promoted via the admin screen.

  function signout() {
    genRef.current++;   // drop any boot/profile answer still in flight
    setLoading(false);  // nothing is loading any more: a boot superseded by this sign-out must not leave the app on its spinner
    api.clearToken();
    setUser(null);
    setIsAdmin(false);
    setVisiblePages(null);
    setRoleName(null);
    setAccessError(null);
    setAccessReady(false);
    // GES-253 — the next person at this browser must not inherit the last person's language.
    setLanguageState(null);
    setLanguageError(null);
    i18n.changeLanguage(BASE_LANGUAGE);
  }

  // GES-253 — THE SWITCHER'S ONE FUNCTION. Flip first, persist second, and say so if the persist
  // fails. Flipping first is what makes the switch feel instant; it is also the honest order,
  // because the UI language is a view concern and the write is what makes it durable. A failed
  // write therefore degrades to "this session only" WITH a visible reason — never to a silent
  // revert (which reads as a broken button) and never to a silent success (which loses the choice
  // at the next reload without telling anyone).
  // 47110 (Codex S1): the writes go out one after another in click order (a slow earlier PATCH can never land last), and only the
  // LATEST click's answer is applied, so the stored value and the screen end on the same language after a quick EN/FR/EN.
  const langWrites = useRef(Promise.resolve());
  const langSeq = useRef(0);
  const changeLanguage = useCallback(async (next) => {
    if (!SUPPORTED_LANGUAGES.includes(next)) return;
    const seq = ++langSeq.current;
    setLanguageState(next);
    setLanguageError(null);
    i18n.changeLanguage(next);
    rememberLanguageHint(next);
    const write = langWrites.current.then(() => api.setMyLanguage(next));
    langWrites.current = write.catch(() => {});
    try {
      const saved = await write;
      if (seq !== langSeq.current) return;   // a later click owns the outcome
      // Trust the read-back, not the request: if the server stored something else, show that.
      if (saved && SUPPORTED_LANGUAGES.includes(saved.language) && saved.language !== next) {
        setLanguageState(saved.language);
        i18n.changeLanguage(saved.language);
        rememberLanguageHint(saved.language);
      }
    } catch (err) {
      if (seq !== langSeq.current) return;
      setLanguageError(err?.message || 'The language preference could not be saved.');
    }
  }, []);

  // One ctx object, one identity, so the memoised helpers below only change when access does.
  const accessCtx = useMemo(() => ({ isAdmin, visiblePages }), [isAdmin, visiblePages]);
  const isVisible    = useCallback((key)     => pageVisible(key, accessCtx),        [accessCtx]);
  const isTabVisible = useCallback((p, t)    => tabVisible(p, t, accessCtx),        [accessCtx]);
  const landingPage  = useMemo(()            => firstVisiblePage(accessCtx),        [accessCtx]);

  return (
    <AuthContext.Provider value={{
      user, isAdmin, loading, error, signin, signout,
      // GES-252 P4b
      visiblePages, roleName, accessError, accessReady,
      isVisible, isTabVisible, landingPage, reloadAccess: loadProfile,
      // GES-253
      language, languageError, changeLanguage,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
